import { makeFullAddress } from './common';
import { macroSpecialities } from '@davincihealthcare/booking-synonyms';
import { DeliveryModeTypes } from '@/costants/enum';
import { ImageItem } from '@/app/_components/images/types';
import {
  AggregatorSpeciality,
  Geolocalization,
  Image,
  License,
  Maybe,
  OpeningHours,
  PaymentType,
  SpecialityChannel,
} from '@/types-aggregatore';
import { capitalizeWordsInString } from '@/utils/string';
import { AggregatorWorkgroupInclinic } from '@/app/_components/widgets/profile/types';

export const translateSpecialization = (specialization?: Maybe<string>) => {
  if (!specialization) {
    return null;
  }
  const translation = macroSpecialities[specialization]?.label;
  return translation ?? null;
};

export const transformSpecializations = (specializations?: Maybe<Maybe<AggregatorSpeciality>[]>) => {
  if (!specializations?.length) {
    return [];
  }
  const list: any[] = [];
  for (const specialization of specializations) {
    if (list.find(item => item.value === translateSpecialization(specialization?.macroSpeciality)) || !specialization?.macroSpeciality) {
      continue;
    }
    const listItem = {
      value: translateSpecialization(specialization?.macroSpeciality) ?? '',
    };
    list.push({ ...listItem, ...(specialization?.id && { key: specialization.id }) });
  }
  return list;
};

export const makeProfile = ({ ambulatory }: { ambulatory: AggregatorWorkgroupInclinic }) => {
  const cityName = capitalizeWordsInString(ambulatory.address?.cityName);
  const license = ambulatory.clinic.license ?? License.None;
  return {
    id: ambulatory.id,
    domiciliaryCare: ambulatory.domiciliaryCare ?? false,
    name: ambulatory.name,
    imageSrc: ambulatory.logoUrl ?? undefined,
    completeAddress: makeFullAddress(ambulatory.address?.street, ambulatory.address?.cap, cityName, ambulatory.address?.streetNumber) ?? '',
    address: ambulatory.address,
    location: makeLocation(ambulatory.address?.location),
    city: cityName,
    phones: getPhoneNumbers(ambulatory.phones),
    description: ambulatory.description ?? undefined,
    isLicensed: license !== License.None,
    availableSpecializations: getAvailableSpecializations(ambulatory.specializations),
    availableOpeningHours: getAvailableOpeningHours(ambulatory.openingHours),
    availablePayments: getAvailablePayments(ambulatory.availablePayments),
    availableDeliveryModes: getAvailableDeliveryModes(ambulatory),
    photos: getPhotos(ambulatory.seo?.images ?? []),
    license: license,
    inclinicSlug: ambulatory.inclinicSlug,
    seo: ambulatory.seo,
  };
};

export type Profile = ReturnType<typeof makeProfile>;

export const getPhoneNumbers = (phones?: Maybe<Maybe<string>[]>) => {
  const phoneNumbers: string[] = [];
  for (const phoneNumber of phones ?? []) {
    if (!phoneNumber) continue;
    phoneNumbers.push(phoneNumber);
  }
  return phoneNumbers;
};

export const getAvailableSpecializations = (specializations?: Maybe<Maybe<AggregatorSpeciality>[]>) => {
  const availableSpecializations: AggregatorSpeciality[] = [];
  for (const specialization of specializations ?? []) {
    if (!specialization) continue;
    availableSpecializations.push(specialization);
  }
  return availableSpecializations;
};

export const getAvailableOpeningHours = (openingHours?: Maybe<Maybe<OpeningHours>[]>) => {
  const availableOpeningHours: OpeningHours[] = [];
  for (const openingHour of openingHours ?? []) {
    if (!openingHour) continue;
    availableOpeningHours.push(openingHour);
  }
  return availableOpeningHours;
};

export const getAvailablePayments = (payments?: Maybe<Maybe<PaymentType>[]>): PaymentType[] => {
  const availablePayments = [];
  for (const payment of payments ?? []) {
    if (!payment) continue;
    availablePayments.push(payment);
  }
  return availablePayments;
};

export const getAvailableDeliveryModes = (clinicGroup: AggregatorWorkgroupInclinic): DeliveryModeTypes[] => {
  const availableDeliveryModes = [];
  if (clinicGroup.domiciliaryCare) {
    availableDeliveryModes.push(DeliveryModeTypes.Domiciliary);
  } else if (clinicGroup.specializations?.some(specialization => specialization?.specialityChannels?.includes(SpecialityChannel.Onsite))) {
    availableDeliveryModes.push(DeliveryModeTypes.Onsite);
  }
  if (clinicGroup.specializations?.some(specialization => specialization?.specialityChannels?.includes(SpecialityChannel.Online)))
    availableDeliveryModes.push(DeliveryModeTypes.Online);
  return availableDeliveryModes;
};

export const getPhotos = (images: (Image | null)[]): ImageItem[] => {
  const photos = [];
  for (const image of images ?? []) {
    if (image && image.url) {
      photos.push({ href: image.url, alt: image.altText ?? '' });
    }
  }

  return photos;
};

export const makeLocation = (location: Maybe<Geolocalization> | undefined) => {
  if (!location?.coordinates?.[0] || !location?.coordinates?.[1]) return;
  return {
    longitude: location.coordinates[0],
    latitude: location.coordinates[1],
  };
};
